"use client";

import Link from "next/link";
import { SearchResult, getContentSnippet } from "./help-search-index";
import { FileText, ChevronRight, Search } from "lucide-react";

interface HelpSearchResultsProps {
  results: SearchResult[];
  query: string;
  isSearching: boolean;
}

/**
 * Highlight matching query text in a string
 */
function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) {
    return text;
  }

  const parts = text.split(new RegExp(`(${query})`, "gi"));

  return parts.map((part, index) => {
    const isMatch = part.toLowerCase() === query.toLowerCase();
    return isMatch ? (
      <mark key={index} className="bg-yellow-200 dark:bg-yellow-900/50 text-gray-900 dark:text-white font-medium">
        {part}
      </mark>
    ) : (
      <span key={index}>{part}</span>
    );
  });
}

/**
 * Get category badge color based on category
 */
function getCategoryColor(category: string): string {
  switch (category) {
    case "getting-started":
      return "bg-success/10 text-success dark:bg-green-900/30 dark:text-green-400";
    case "features":
      return "bg-info/10 text-info dark:bg-blue-900/30 dark:text-info";
    case "guides":
      return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400";
  }
}

/**
 * Format category name for display
 */
function formatCategory(category: string): string {
  return category
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Display search results with highlighting and empty states
 */
export function HelpSearchResults({
  results,
  query,
  isSearching,
}: HelpSearchResultsProps) {
  // Empty state - no query
  if (!query.trim()) {
    return (
      <div className="text-center py-12">
        <Search className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Search our help center
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Type keywords to find articles, guides, and documentation
        </p>
      </div>
    );
  }

  // Empty state - no results found
  if (!isSearching && results.length === 0) {
    return (
      <div className="text-center py-12">
        <Search className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No results found for &quot;{query}&quot;
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Try different keywords or browse our help categories below
        </p>
        <div className="flex flex-wrap gap-2 justify-center">
          <Link
            href="/help"
            className="px-4 py-2 bg-info text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Browse All Articles
          </Link>
        </div>
      </div>
    );
  }

  // Results found
  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Found {results.length} {results.length === 1 ? "article" : "articles"} matching &quot;{query}&quot;
      </div>

      <div className="space-y-3">
        {results.map((result, index) => {
          const { article, matches } = result;
          const snippet = getContentSnippet(article.content, query);

          return (
            <Link
              key={`${article.slug}-${index}`}
              href={`/help/${article.slug}`}
              className="block group"
            >
              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-info dark:hover:border-blue-600 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Category badge */}
                    <div className="mb-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(
                          article.category
                        )}`}
                      >
                        {formatCategory(article.category)}
                      </span>
                    </div>

                    {/* Title with highlighting */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-info dark:group-hover:text-info transition-colors">
                      {highlightText(article.title, query)}
                    </h3>

                    {/* Description with highlighting */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {highlightText(article.description, query)}
                    </p>

                    {/* Matching headings */}
                    {matches.inHeadings.length > 0 && (
                      <div className="mb-2">
                        <div className="text-xs text-gray-500 dark:text-gray-500 mb-1">
                          Matches in:
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {matches.inHeadings.slice(0, 3).map((heading, idx) => (
                            <span
                              key={idx}
                              className="inline-block px-2 py-0.5 bg-warning/10 dark:bg-yellow-900/30 text-warning dark:text-yellow-400 rounded text-xs"
                            >
                              {highlightText(heading, query)}
                            </span>
                          ))}
                          {matches.inHeadings.length > 3 && (
                            <span className="inline-block px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded text-xs">
                              +{matches.inHeadings.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Content snippet with highlighting */}
                    {matches.inContent && (
                      <p className="text-sm text-gray-500 dark:text-gray-500 line-clamp-2">
                        {highlightText(snippet, query)}
                      </p>
                    )}
                  </div>

                  <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-info transition-colors flex-shrink-0 mt-1" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Show helpful tip if many results */}
      {results.length > 5 && (
        <div className="mt-6 p-4 bg-info/10 dark:bg-blue-900/20 rounded-lg border border-info dark:border-blue-800">
          <p className="text-sm text-info dark:text-blue-300">
            <strong>Tip:</strong> Try more specific keywords to narrow down your search results.
          </p>
        </div>
      )}
    </div>
  );
}
